import { FC, HTMLProps } from 'react';
import styles from './Table.module.css';
import UpIcon from '../../atoms/UpIcon';
import DownIcon from '../../atoms/DownIcon';
import { translate } from '../../../localization';
import { SortOrder } from '../../../constants/common';
import { InfoTooltip, TooltipPosition } from '../InfoTooltip';

interface TBaseProps extends HTMLProps<HTMLDivElement> {
  children: React.ReactNode;
}

interface THeadCellProps extends TBaseProps {
  name: string;
  isSortable?: boolean;
  isSorted?: boolean;
  sortOrder?: SortOrder;
  showInfoIcon?: boolean;
  infoText?: string;
  tooltipPosition?: TooltipPosition;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick: (e?: any) => void;
}

interface TCaptionProps extends TBaseProps {
  caption: string;
}

interface TRowProps extends TBaseProps {
  rowRef: React.Ref<HTMLDivElement>;
}

export const THead: FC<TBaseProps> = ({ children, ...rest }) => (
  <div role='rowgroup' {...rest}>
    {children}
  </div>
);

export const THeadRow: FC<TRowProps> = ({
  children,
  rowRef,
  className,
  ...rest
}) => (
  <div
    role='row'
    className={`tab-accessible ${styles['table__head-row']} ${className || ''}`}
    tabIndex={0}
    ref={rowRef}
    {...rest}
  >
    {children}
  </div>
);

export const THeadCell: FC<THeadCellProps> = ({
  name,
  children,
  className,
  isSortable = false,
  isSorted = false,
  sortOrder = SortOrder.ASCENDING,
  onClick,
  showInfoIcon = false,
  infoText = '',
  tooltipPosition = TooltipPosition.BOTTOM_LEFT,
  ...rest
}) => {
  const onKeyDownHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault(); // To prevent scrolling on spacebar
      onClick();
    }
  };

  const tooltipId =
    name && name != null
      ? `${name?.toLowerCase().replace(' ', '-')}-info-icon`
      : 'table-head-info-icon';

  return (
    <div
      role='columnheader'
      className={`${styles['table__head-cell']} ${className || ''}`}
      {...rest}
    >
      <div
        className={`tab-accessible ${
          isSortable ? styles['table__head-cell-sortable'] : ''
        }`}
        onClick={onClick}
        onKeyDown={onKeyDownHandler}
        tabIndex={0}
        role='button'
      >
        {children}
      </div>

      {showInfoIcon && (
        <InfoTooltip
          id={tooltipId}
          content={infoText}
          tooltipPosition={tooltipPosition}
          classes={styles['table__head-cell__tooltip']}
        />
      )}

      {isSortable && (
        <div
          className={styles.table__sort}
          onClick={onClick}
          onKeyDown={onKeyDownHandler}
          tabIndex={-1}
          role='button'
        >
          <UpIcon
            tabIndex={0}
            aria-label={`${translate('sort_in_ascending', {
              field: name,
            })}`}
            className={`tab-accessible ${
              isSorted && sortOrder === SortOrder.ASCENDING
                ? styles['table__icon-sort-state']
                : ''
            }`}
            data-order='asc'
          />

          <DownIcon
            tabIndex={0}
            aria-label={`${translate('sort_in_descending', {
              field: name,
            })}`}
            className={
              isSorted && sortOrder === SortOrder.DESCENDING
                ? styles['table__icon-sort-state']
                : ''
            }
            data-order='desc'
          />
        </div>
      )}
    </div>
  );
};

export const TBody: FC<TBaseProps> = ({ children, className, ...rest }) => (
  <div
    role='rowgroup'
    tabIndex={0}
    aria-label='Table contents'
    className={`tab-accessible ${styles.table__body} ${className || ''}`}
    {...rest}
  >
    {children}
  </div>
);

export const TRow: FC<TRowProps> = ({
  children,
  className,
  rowRef,
  ...rest
}) => {
  return (
    <div
      role='row'
      tabIndex={0}
      className={`tab-accessible ${styles.table__row} ${className || ''}`}
      ref={rowRef}
      {...rest}
    >
      {children}
    </div>
  );
};

export const TCell: FC<TBaseProps> = ({ children, className, ...rest }) => (
  <div
    role='cell'
    tabIndex={0}
    className={`tab-accessible ${styles.table__cell} ${className || ''}`}
    {...rest}
  >
    <div>{children}</div>
  </div>
);

export const TEmptyCell: FC<HTMLProps<HTMLDivElement>> = ({
  className,
  ...rest
}) => (
  <div
    className={`${styles.table__cell} ${className || ''}`}
    aria-hidden='true'
    tabIndex={-1}
    {...rest}
  />
);

export const TCaption: FC<TCaptionProps> = ({
  caption,
  className,
  ...rest
}) => (
  <div
    className={`${styles.table__caption} ${className || ''}`}
    {...rest}
    role='caption'
  >
    {caption}
  </div>
);

const Table: FC<TBaseProps> = ({ children, className, ...rest }) => {
  return (
    <div
      tabIndex={0}
      role='table'
      className={`tab-accessible ${styles.table} ${className || ''}`}
      {...rest}
    >
      {children}
    </div>
  );
};

Table.displayName = 'Table';

export default Table;
